import { NavigationGuardNext, RouteLocationNormalized } from "vue-router";
import { useAuth } from "./useAuth";

export interface AuthGuardResult {
  allowed: boolean;
  redirectTo?: { name: string; replace?: boolean };
  reason?: string;
}

/**
 * Centralized auth guard logic for route protection.
 * Separates route-level auth concerns from MSAL integration.
 */
export function useAuthGuard() {
  
  /**
   * Check if current URL indicates we're in an auth redirect callback
   */
  const isAuthRedirectCallback = (): boolean => {
    const url = new URL(window.location.href);
    // Check for common OIDC/OAuth redirect parameters
    const redirectParams = ["code", "state", "session_state"];
    return redirectParams.some(param => url.searchParams.has(param));
  };

  /**
   * Check if a route should be accessible based on auth state
   */
  const checkRouteAccess = async (to: RouteLocationNormalized): Promise<AuthGuardResult> => {
    const { isInitialized, isAuthenticated, isAuthorized, initializeMsal } = useAuth();

    // Ensure MSAL is initialized before making auth decisions
    if (!isInitialized.value) {
      try {
        await initializeMsal();
      } catch (error) {
        console.error("AuthGuard: MSAL initialization failed:", error);
        // Continue to allow routing to login page even if initialization fails
      }
    }

    // Routes that don't require authentication
    if (!to.meta.requiresAuth) {
      // Special case: If user is trying to access login but we're in a redirect callback,
      // allow it so MSAL can process the redirect properly
      if (to.name === "login" && isAuthRedirectCallback()) {
        console.log("AuthGuard: Allowing login route access during auth redirect callback");
        return { allowed: true };
      }
      
      // If user is already authenticated and trying to access login, redirect to home
      // BUT only if we're not in a redirect callback scenario
      if (to.name === "login" && isAuthenticated.value) {
        return {
          allowed: false,
          redirectTo: { name: "home", replace: true },
          reason: "Already authenticated, redirecting to home"
        };
      }
      return { allowed: true };
    }

    // Routes that require authentication
    if (!isAuthenticated.value) {
      return {
        allowed: false,
        redirectTo: { name: "login", replace: true },
        reason: "Authentication required"
      };
    }

    // Routes that require authorization
    if (to.meta.requiresAuthorization && !isAuthorized.value) {
      return {
        allowed: false,
        redirectTo: { name: "unauthorized" },
        reason: "Authorization required but user is not authorized"
      };
    }

    return { allowed: true };
  };

  /**
   * Navigation guard function to be used in router
   */
  const authGuard = async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ): Promise<void> => {
    const result = await checkRouteAccess(to);
    
    if (result.allowed) {
      next();
    } else if (result.redirectTo) {
      console.log(`AuthGuard: ${result.reason}, redirecting to ${result.redirectTo.name}`);
      next(result.redirectTo);
    } else {
      // Fallback - should not happen with current logic
      console.warn("AuthGuard: Access denied but no redirect specified");
      next(false);
    }
  };

  return {
    checkRouteAccess,
    authGuard,
    isAuthRedirectCallback // Export for testing
  };
}